import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  Grid,
  Link,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
} from "@material-ui/core";
import Button from "components/shared/button";
import DefaultCloseIcon from "src/images/default-x-close.svg";
import ChevronRightIcon from "src/images/icons/chevron-right.svg";
import ChevronLeftIcon from "src/images/icons/chevron-left.svg";
import LinkIcon from "components/shared/icons/link";
import colors from "components/shared/colors";
import CalloutSection from "components/shared/callout_section";
import CheckboxWithLabel from "components/portal/shared/selectors/checkbox_with_label";
import Dialog from "components/shared/dialog";
import { SimpleTextForm } from "components/shared/inputs";
import { Document, Page, pdfjs } from "react-pdf";
import { getFileOnNewTab } from "src/javascript/clients/shared/sawyer_client";
import { fetchURLAgreementPDF } from "components/portal/listings/agreements/agreements_client";
import ReadOnlyRichText from "components/shared/read_only_rich_text";
import "react-pdf-text-layer.css";
import "react-pdf-annotation-layer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const styles = (theme) => ({
  agreementModalContainer: {
    display: "flex",
    flexDirection: "column",
  },
  columnWithGap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    width: "100%",
  },
  columnWithMediumGap: {
    display: "flex",
    flexDirection: "column",
    marginTop: -theme.spacing(3),
    width: "100%",

    [theme.breakpoints.only("xs")]: {
      marginTop: "0",
    },
  },
  content: {
    padding: theme.spacing(1, 5, 10, 5),
    width: "100%",
    gap: theme.spacing(5),

    [theme.breakpoints.only("xs")]: {
      padding: theme.spacing(1, 2, 10, 2),
      gap: theme.spacing(3),
    },
  },
  title: {
    padding: theme.spacing(0, 5),
    textAlign: "center",
    fontWeight: 700,
    color: colors.grey700,
    fontSize: theme.typography.pxToRem(24),
    // Have to use !important because mui v4 doesn't allow to create new typography variants. 💀
    lineHeight: "32px !important",
  },
  close: {
    display: "flex",
    padding: theme.spacing(3, 3, 1, 3),
    justifyContent: "flex-end",
    width: "100%",
  },
  closeButton: {
    cursor: "pointer",
    fill: colors.grey500,
    "&:hover": {
      fill: colors.grey600,
    },
    fontSize: theme.typography.pxToRem(24),
  },
  dividerhorizontal: {
    height: "1px",
    backgroundColor: "#e5e5e5",

    [theme.breakpoints.only("xs")]: {
      display: "none",
    },
  },
  openPdfButton: {
    borderRadius: "2px",
    border: "solid 1px #e5e5e5",
    backgroundColor: "#fafafa",
    height: "40px",

    [theme.breakpoints.only("xs")]: {
      marginBottom: "16px",
      marginTop: "-8px",
    },
  },
  openPdfTextButton: {
    padding: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "0.5px",
    color: "#595959",
  },
  signatureSection: {
    padding: theme.spacing(0, 5),
    overflow: "hidden",

    [theme.breakpoints.only("xs")]: {
      padding: theme.spacing(0, 2),
    },
  },
  signatureHeader: {
    color: colors.grey700,
    // Have to use !important because mui v4 doesn't allow to create new typography variants. 💀
    fontSize: "20px !important",
    lineHeight: "24px !important",
  },
  columnWithSmallGap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  centerAlign: {
    display: "flex",
    alignItems: "center",
  },
  inputsContainer: {
    gap: theme.spacing(3),
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
    },
  },
  fullName: {
    flex: 2,
  },
  date: {
    flex: 1,
  },
  inputDisabled: {
    color: colors.grey600,
    backgroundColor: colors.grey100,

    "& fieldset": {
      borderColor: `${colors.vanillaDark} !important`,
    },

    "&:hover": {
      "& fieldset": {
        borderWidth: "1px !important",
      },
    },
  },
  agreementCallout: {
    padding: theme.spacing(2),
  },
  signaturePreviewCallout: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(4, 2),
    height: "120px",
    overflowY: "auto",
  },
  signature: {
    fontFamily: "Caveat",
    lineHeight: "56px",
    fontSize: theme.typography.pxToRem(40),
    cursor: "default",
    height: "100%",
    width: "100%",
    overflowWrap: "break-word",
  },
  submitButton: {
    padding: theme.spacing(0, 5),

    [theme.breakpoints.only("xs")]: {
      padding: theme.spacing(0, 1),
    },
  },
  checkboxInput: {
    minHeight: "unset",
    height: "24px",
    width: "24px",
    position: "absolute",
    backgroundColor: "transparent",
    cursor: "pointer",
  },
  dialogPaper: {
    padding: 0,
    flexGrow: 1,
    maxWidth: "928px",
    overflowX: "hidden",
  },
  checkboxControlRoot: ({ signed }) => ({
    // This class is an ugly hack to make the checkbox align with the text while preserving hover effect roundness.
    width: "fit-content",
    alignItems: "flex-start",
    margin: 0,
    cursor: signed ? "default" : "pointer",

    "& div": {
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
  }),
  hideBlock: {
    display: "none !important",
  },
  checkboxButtonBase: {
    padding: 0,
  },
  arrowButton: {
    cursor: "pointer",
    fill: colors.grey500,
    "&:hover": {
      fill: colors.grey600,
    },
    fontSize: theme.typography.pxToRem(13),
  },
  link: {
    cursor: "pointer",
    fill: colors.grey500,
  },
});

const formatDate = (date) => {
  const dateInstance = new Date(date);

  // Create the date in UTC to avoid timezone conversion issues
  const utcDate = new Date(
    Date.UTC(
      dateInstance.getFullYear(),
      dateInstance.getMonth(),
      dateInstance.getDate()
    )
  );

  // Convert to the desired locale string without timezone conversion
  return utcDate.toLocaleString("default", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const SignAgreementModal = ({
  open,
  onClose,
  title,
  content = "",
  signed = false,
  signedAt,
  signature = "",
  agreementId,
  childId,
  withFile,
  onSubmit,
  classes,
  clientAccount,
  trackTermsAcceptance,
  trackPDFDownload,
}) => {
  const [signatureText, setSignatureText] = useState(signature);
  const [isAgreed, setIsAgreed] = useState(signed);
  const [errorMessage, setErrorMessage] = useState("");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isDesktop = useMediaQuery("(min-width: 769px)");
  const handleTextSignatureChange = (e) => {
    const newText = e.target.value;
    setSignatureText(newText);

    if (hasNonWhitespaceSignatureText(newText)) {
      setErrorMessage("");
    } else {
      setErrorMessage("Please enter your full name");
    }
  };

  const [pdfData, setPdfData] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const loading = <div className="pdf-loading">Loading...</div>;
  const handleAgreeChange = () => {
    setIsAgreed((prev) => {
      !prev ? trackTermsAcceptance() : null;
      return !prev;
    });
  };

  const handleDownload = (id, childId, withFile) => {
    const baseUrl = `/api/v1/account/agreement_document_pdfs/${id}?without_signature=true${
      childId ? `&child_id=${childId}` : ""
    }`;
    getFileOnNewTab(
      baseUrl,
      "application/octet-stream",
      () => {},
      () => {},
      "GET",
      withFile
    );
    trackPDFDownload();
  };

  const assignUrlPdf = (fileURL) => {
    setPdfData(fileURL);
  };

  useEffect(() => {
    if (agreementId && open && !content) {
      fetchURLAgreementPDF(
        agreementId,
        assignUrlPdf,
        () => {},
        { child_id: childId },
        true,
        true,
        clientAccount
      );
    } else if (!open) {
      setPdfData(null);
      setNumPages(null);
      setPageNumber(1);
    }
  }, [agreementId, open]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  function changePage(offset) {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }

  function previousPage() {
    if (!(pageNumber <= 1)) {
      changePage(-1);
    }
  }

  function nextPage() {
    if (!(pageNumber >= numPages)) {
      changePage(1);
    }
  }

  const hasNonWhitespaceSignatureText = (signatureText) => {
    return signatureText.trim().length > 0;
  };

  useEffect(() => {
    setSignatureText(signature);
    setIsAgreed(signed);
  }, [signed, signature]);

  return (
    <Dialog open={open} classes={{ paper: classes.dialogPaper }}>
      <Grid container className={classes.agreementModalContainer}>
        <Grid item className={classes.close}>
          <SvgIcon
            component={DefaultCloseIcon}
            onClick={onClose}
            className={classes.closeButton}
          />
        </Grid>
        <Grid
          item
          container
          className={`${classes.content} ${classes.columnWithGap}`}
        >
          <Grid item>
            <Typography variant="h2" className={classes.title}>
              {title}
            </Typography>
          </Grid>
          <Grid item className={`${pdfData ? "pdf-content" : ""}`}>
            {!pdfData && <ReadOnlyRichText content={content} />}
            {pdfData && (
              <Document
                key={pdfData}
                file={pdfData}
                onLoadSuccess={onDocumentLoadSuccess}
                style={classes.pdfContent}
              >
                <Page
                  key={`page-${pageNumber}`}
                  size="A4"
                  className="pdf-page-content"
                  loading={loading}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Document>
            )}
            {pdfData && numPages > 1 && (
              <Grid item className="pdf-pages">
                <SvgIcon
                  component={ChevronLeftIcon}
                  onClick={previousPage}
                  className={classes.arrowButton}
                />
                <span className="text-pages">
                  {pageNumber} of {numPages}
                </span>
                <SvgIcon
                  component={ChevronRightIcon}
                  onClick={nextPage}
                  className={classes.arrowButton}
                />
              </Grid>
            )}
          </Grid>
          <Grid container item className={`${classes.columnWithMediumGap}`}>
            <Button
              fullWidth
              color="default"
              onClick={() => handleDownload(agreementId, childId, withFile)}
              variant="contained"
              className={classes.openPdfButton}
            >
              <SvgIcon component={LinkIcon} className={classes.link} />
              <Typography variant="body1" className={classes.openPdfTextButton}>
                Open PDF in browser
              </Typography>
            </Button>
          </Grid>
          <Grid
            container
            item
            className={`${classes.signatureSection} ${classes.columnWithGap}`}
          >
            <Grid item>
              <Typography variant="h3" className={classes.signatureHeader}>
                Signature
              </Typography>
            </Grid>
            <Grid item className={classes.columnWithGap}>
              <CalloutSection>
                <Box
                  className={`${classes.columnWithSmallGap} ${classes.agreementCallout}`}
                >
                  <Box>
                    <Typography variant="h4">
                      {`By signing this document I am agreeing to the terms
                      outlined in the above agreement.
                      ${
                        !isDesktop
                          ? "I certify that my electronic signature is as valid as my physical signature."
                          : ""
                      } `}
                    </Typography>
                    {isDesktop && (
                      <Typography variant="h4">
                        I certify that my electronic signature is as valid as my
                        physical signature.
                      </Typography>
                    )}
                  </Box>
                  <FormControl>
                    <CheckboxWithLabel
                      isDisabled={signed}
                      checked={isAgreed}
                      classes={{ root: classes.checkboxControlRoot }}
                      inputProps={{ className: classes.checkboxInput }}
                      checkboxClasses={{ root: classes.checkboxButtonBase }}
                      checkboxValue={isAgreed}
                      onCheckboxChange={handleAgreeChange}
                      checkboxLabel={
                        <Typography variant="body1">
                          I agree to electronically sign this document.&nbsp;
                          <span style={{ color: colors.poppyDefault }}>*</span>
                        </Typography>
                      }
                    />
                  </FormControl>
                </Box>
              </CalloutSection>
              <Grid item container className={classes.inputsContainer}>
                <Grid
                  item
                  className={`${classes.columnWithSmallGap} ${classes.fullName}`}
                >
                  <Box className={classes.centerAlign}>
                    <Typography variant="h4">Full Name&nbsp;</Typography>
                    <span style={{ color: colors.poppyDefault }}>*</span>
                  </Box>
                  <Box className={classes.columnWithGap}>
                    <SimpleTextForm
                      fullWidth
                      disabled={signed}
                      value={signatureText}
                      InputProps={{
                        classes: {
                          disabled: classes.inputDisabled,
                        },
                      }}
                      onChange={handleTextSignatureChange}
                      variant="outlined"
                      placeholder="Type full name"
                      errorText={errorMessage}
                    />
                    {isXs ? (
                      <Grid item className={classes.columnWithSmallGap}>
                        <Typography variant="h4">Signature Preview</Typography>
                        <CalloutSection>
                          <Box className={classes.signaturePreviewCallout}>
                            <Typography className={classes.signature}>
                              {signatureText}
                            </Typography>
                          </Box>
                        </CalloutSection>
                      </Grid>
                    ) : null}
                  </Box>
                </Grid>
                <Grid
                  item
                  className={`${classes.columnWithSmallGap} ${classes.date}`}
                >
                  <Typography variant="h4">Date</Typography>
                  <SimpleTextForm
                    disabled
                    fullWidth
                    InputProps={{
                      classes: {
                        disabled: classes.inputDisabled,
                      },
                    }}
                    value={formatDate(signed ? signedAt : new Date())}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              {isXs ? null : (
                <Grid item className={classes.columnWithSmallGap}>
                  <Typography variant="h4">Signature Preview</Typography>
                  <CalloutSection>
                    <Box className={classes.signaturePreviewCallout}>
                      <Typography className={classes.signature}>
                        {signatureText}
                      </Typography>
                    </Box>
                  </CalloutSection>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item className={classes.submitButton}>
            <Button
              fullWidth
              color="primary"
              disabled={
                !isAgreed ||
                !signatureText ||
                !hasNonWhitespaceSignatureText(signatureText)
              }
              onClick={() =>
                signed ? onClose() : onSubmit(signatureText.trimStart())
              }
              variant="contained"
            >
              {signed ? "Close" : "Agree & Submit"}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

SignAgreementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  signed: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  clientAccount: PropTypes.bool,
  trackTermsAcceptance: PropTypes.func,
  trackPDFDownload: PropTypes.func,
};

export default withStyles(styles)(SignAgreementModal);
